import { supabase } from "../supabaseClient";
import { genId } from "./helpers";
import { withRetry, serverNow } from "./net";

// ─────────────────────────────────────────
//  Per-group token (M1/M2/M3): a secret returned by join_group and required by
//  record_scratch/finish_group and the appeal/BQ/delegation RPCs. Kept in
//  localStorage keyed by session+group so it survives reload and is shared
//  between the join screen and the play screen. Never read from the DB by the
//  client — the token lives in an isolated table only the RPCs can touch.
// ─────────────────────────────────────────
const groupTokenKey = (sessionId, groupName) => `trat-token-${sessionId}-${groupName}`;
function getGroupToken(sessionId, groupName) {
  try { return localStorage.getItem(groupTokenKey(sessionId, groupName)); } catch { return null; }
}
function setGroupToken(sessionId, groupName, token) {
  if (!token) return;
  try { localStorage.setItem(groupTokenKey(sessionId, groupName), token); } catch {}
}

// group_ref: gruppens ogissbara referens i group_progress. Den tabellen är
// anonymt läsbar (realtidssynken kräver det) och innehåller därför inte längre
// gruppnamnet — klienten filtrerar realtime-event på sin egen ref i stället.
const groupRefKey = (sessionId, groupName) => `trat-ref-${sessionId}-${groupName}`;
function getGroupRef(sessionId, groupName) {
  try { return localStorage.getItem(groupRefKey(sessionId, groupName)); } catch { return null; }
}
// Gruppens ref från servern (token-skyddat), sparad lokalt. null om den inte
// gick att få (funktionen saknas, fel token) — då beter sig klienten som förut.
async function dbEnsureGroupRef(sessionId, groupName) {
  const existing = getGroupRef(sessionId, groupName);
  if (existing) return existing;
  const { data, error } = await supabase.rpc("get_group_ref", {
    p_code: sessionId, p_group: groupName, p_token: getGroupToken(sessionId, groupName),
  });
  if (error || !data) return null;
  setGroupRef(sessionId, groupName, data);
  return data;
}

function setGroupRef(sessionId, groupName, ref) {
  if (!ref) return;
  try { localStorage.setItem(groupRefKey(sessionId, groupName), ref); } catch {}
}

// När en grupp lämnar tRAT:en glömmer enheten den, så att nästa elev på en
// delad iPad inte kan gå rakt in i gruppen. Tillbaka kommer man med
// sessionskoden (och gruppkoden, om tRAT:en har sådana).
function forgetGroup(sessionId, groupName) {
  try {
    localStorage.removeItem(groupTokenKey(sessionId, groupName));
    localStorage.removeItem(groupRefKey(sessionId, groupName));
  } catch {}
}

// ─────────────────────────────────────────
//  Supabase data functions
// ─────────────────────────────────────────
// Parsern lämnar facit i varje frågeobjekt (correctAnswer m.fl.). Rätt svar hör
// hemma i answer_key — kolumnen som studentens RPC aldrig returnerar — så de
// nycklarna får inte följa med in i questions-jsonb:en. Serversidan strippar
// dem också (20260828000000), men skriv inte ned dem från början.
function stripAnswerKeys(questions) {
  if (!Array.isArray(questions)) return questions;
  return questions.map(q => {
    if (!q || typeof q !== "object") return q;
    const { correctAnswer, correctAnswerLabel, correctAnswerIndex, isCorrect, ...rest } = q;
    return rest;
  });
}

async function dbSaveSession(sessionData, userId) {
  // INSERT (not upsert): a random-id collision must fail loudly instead of
  // silently overwriting another teacher's session. Callers retry with new ids.
  return withRetry(async () => {
    const { error } = await supabase.from("sessions").insert({
      id: sessionData.sessionId, dash_id: sessionData.dashId, leaderboard_id: sessionData.leaderboardId,
      teacher_id: userId || null,
      answer_key: sessionData.answerKey, num_questions: sessionData.numQ,
      num_options: sessionData.numOptions, questions: stripAnswerKeys(sessionData.questions), show_rationale: false,
      session_title: sessionData.sessionTitle || null,
      available_at: sessionData.availableAt || null,
      config: sessionData.config || {},
      course_instance_id: sessionData.courseInstanceId || null,
    });
    if (error) throw error;
  }, { label: "saveSession" });
}

// ─────────────────────────────────────────
//  Question images (Storage)
// ─────────────────────────────────────────
// Figures extracted from a QPS PDF arrive as blobs on question.images. Upload
// them under the session id and swap in public URLs, so what we store in
// sessions.questions is just URLs — never base64, which would bloat the jsonb.
async function dbUploadQuestionImages(sessionId, questions) {
  if (!Array.isArray(questions)) return questions;
  const out = [];
  for (let qi = 0; qi < questions.length; qi++) {
    const q = questions[qi] || {};
    const imgs = Array.isArray(q.images) ? q.images : null;
    if (!imgs || imgs.length === 0) {
      // Strip any empty images key so it doesn't end up in the DB
      const { images, ...rest } = q;
      out.push(images && images.length ? q : rest);
      continue;
    }
    const urls = [];
    for (let i = 0; i < imgs.length; i++) {
      const img = imgs[i];
      let blob = img?.blob || null;
      if (!blob && typeof img === "string") {
        // Already under this session's own prefix — nothing to do
        if (img.includes(`/${sessionId}/`)) { urls.push(img); continue; }
        // A URL from somewhere else (imported from the question bank, or a
        // duplicated tRAT): take our own copy, so the session doesn't break if
        // that source is later deleted.
        try {
          const res = await fetch(img);
          if (res.ok) blob = await res.blob();
        } catch (err) { console.error("dbUploadQuestionImages fetch:", err); }
        if (!blob) { urls.push(img); continue; } // hellre delad referens än ingen bild
      }
      if (!blob) continue;
      const path = `${sessionId}/q${qi + 1}-${i + 1}.jpg`;
      const { error } = await supabase.storage.from("question-images")
        .upload(path, blob, { contentType: "image/jpeg", upsert: true });
      if (error) { console.error("uploadQuestionImage:", error); continue; }
      const { data } = supabase.storage.from("question-images").getPublicUrl(path);
      if (data?.publicUrl) urls.push(data.publicUrl);
    }
    out.push({ ...q, images: urls });
  }
  return out;
}

// Remove a session's images when it is deleted, so they don't linger orphaned
async function dbDeleteQuestionImages(sessionId) {
  try {
    const { data: files } = await supabase.storage.from("question-images").list(sessionId);
    if (files && files.length) {
      await supabase.storage.from("question-images")
        .remove(files.map(f => `${sessionId}/${f.name}`));
    }
  } catch (err) {
    console.error("deleteQuestionImages:", err);
  }
}

// Ta bort bilder under sessionens prefix som ingen fråga längre pekar på.
// Körs efter en omimport: det gamla frågesattets figurer blir annars kvar
// föräldralösa i bucketen. Icke-kritisk — fel loggas bara.
async function dbPruneQuestionImages(sessionId, questions) {
  try {
    const keep = new Set();
    for (const q of questions || []) {
      for (const img of q?.images || []) {
        if (typeof img === "string") keep.add(img.split("/").pop().split("?")[0]);
      }
    }
    const { data: files } = await supabase.storage.from("question-images").list(sessionId);
    const stale = (files || []).filter(f => !keep.has(f.name)).map(f => `${sessionId}/${f.name}`);
    if (stale.length) await supabase.storage.from("question-images").remove(stale);
  } catch (err) {
    console.error("pruneQuestionImages:", err);
  }
}

// Create a session, regenerating ids on the (rare) unique-key collision
async function dbCreateSessionWithFreshIds(buildSession, userId) {
  let lastErr = null;
  for (let attempt = 0; attempt < 3; attempt++) {
    const session = buildSession({
      sessionId: genId(6),
      dashId: "DASH-" + genId(8),
      leaderboardId: "LB-" + genId(8),
    });
    try {
      // Upload figures under the final session id and replace the blobs with
      // public URLs before the row is written
      if (Array.isArray(session.questions) && session.questions.some(q => q?.images?.length)) {
        session.questions = await dbUploadQuestionImages(session.sessionId, session.questions);
      }
      await dbSaveSession(session, userId);
      return session;
    } catch (e) {
      lastErr = e;
      const msg = e?.message || "";
      if (e?.code !== "23505" && !msg.includes("duplicate") && !msg.includes("unique")) throw e;
    }
  }
  throw lastErr;
}

// Bara det som kan ändras under en tRAT: lås, återkoppling och lärarens
// sluttid. Läser inte frågorna (det tunga) — se migrationen 20261023.
// { missing: true } om funktionen inte finns än (migrationen inte körd):
// då hämtar anroparen hela sessionen som förut.
async function dbLoadSessionStatus(id) {
  const { data, error } = await supabase.rpc("get_session_status", { code: id });
  if (error) return error.code === "PGRST202" ? { missing: true } : null;
  if (!data) return null;
  return { sessionLocked: !!data.session_locked, showRationale: !!data.show_rationale, timerEnd: data.timer_end || null };
}

// Det studentvyerna behöver hålla aktuellt under en tRAT. Normalt bara den
// lätta statusen; hela sessionen hämtas när återkopplingen precis slagits på
// (motiveringarna följer bara med då) eller om statusfunktionen saknas.
// Returnerar en funktion som uppdaterar ett sessionsobjekt, eller null vid fel.
async function dbLoadLiveSession(id, { hadRationale = false } = {}) {
  const st = await dbLoadSessionStatus(id);
  if (!st) return null;
  if (st.missing || (st.showRationale && !hadRationale)) {
    const s = await dbLoadSession(id);
    if (!s) return null;
    // En avslutad tRAT kommer utan frågor (se closed_session_hides_questions)
    if (s.sessionLocked && s.questions.length === 0) return prev => ({ ...prev, sessionLocked: true });
    return prev => ({ ...prev, config: s.config, sessionLocked: s.sessionLocked, showRationale: s.showRationale, questions: s.questions });
  }
  return prev => {
    const config = { ...(prev.config || {}) };
    if (st.timerEnd) config.timer_end = st.timerEnd; else delete config.timer_end;
    return { ...prev, config, sessionLocked: st.sessionLocked, showRationale: st.showRationale };
  };
}

async function dbLoadSession(id) {
  // Server-side RPC: students get a sanitized session (no answer key, no
  // dash code, no group codes); the dash code returns the full session.
  const { data, error } = await supabase.rpc("get_session_for_code", { code: id });
  if (error || !data) return null;
  return {
    sessionId: data.id, dashId: data.dash_id || null, leaderboardId: data.leaderboard_id || null,
    teacherId: data.teacher_id || null,
    answerKey: data.answer_key || null, numQ: data.num_questions, numOptions: data.num_options || 4,
    questions: data.questions || [], showRationale: data.show_rationale || false,
    sessionLocked: data.session_locked || false,
    sessionTitle: data.session_title || null,
    availableAt: data.available_at || null,
    config: data.config || {},
    courseInstanceId: data.course_instance_id || null,
  };
}

// Map a group_results row (snake_case) to the client result shape
function mapGroupResultRow(d) {
  if (!d) return null;
  return {
    scores: d.scores, attempts: d.attempts, total: d.total, maxScore: d.max_score,
    scratchState: d.scratch_state, inProgress: d.in_progress, timerEnd: d.timer_end || null,
    // Gruppens visningsordning när läraren slagit på slump; null annars.
    shuffle: d.shuffle || null,
  };
}

// Student joins a group — group code and per-group token validated server-side.
// A stored token (reload / same device) re-authenticates without the code; a
// fresh device re-authenticates with the group code and gets the token back.
async function dbJoinGroup(sessionCode, groupName, groupCode) {
  const { data, error } = await supabase.rpc("join_group", {
    p_code: sessionCode, p_group_name: groupName,
    p_group_code: groupCode || null, p_token: getGroupToken(sessionCode, groupName),
  });
  if (error) throw error;
  if (data?.error) return { error: data.error, availableAt: data.available_at || null };
  setGroupToken(sessionCode, groupName, data?.token);
  setGroupRef(sessionCode, groupName, data?.group_ref);
  // join_group returnerar inte refen; hämta den separat (en gång per enhet),
  // så att realtidshändelser från andra grupper kan sorteras bort
  const groupRef = data?.group_ref || (data?.ok ? await dbEnsureGroupRef(sessionCode, groupName) : null);
  return { result: mapGroupResultRow(data?.result), groupRef };
}

// Student scratches an option — scored server-side, answer key never leaves the server
// timeoutMs: ge upp väntan efter så lång tid (kastar). Servern kan ändå ha
// sparat skrapet; record_scratch räknar samma alternativ bara en gång, så det
// är säkert att skicka samma skrap igen.
async function dbRecordScratch(sessionCode, groupName, qi, oi, { timeoutMs } = {}) {
  let q = supabase.rpc("record_scratch", {
    p_code: sessionCode, p_group_name: groupName, p_qi: qi, p_oi: oi,
    p_token: getGroupToken(sessionCode, groupName),
  });
  let timer = null;
  if (timeoutMs) {
    const ctrl = new AbortController();
    timer = setTimeout(() => ctrl.abort(), timeoutMs);
    q = q.abortSignal(ctrl.signal);
  }
  try {
    const { data, error } = await q;
    if (error) throw error;
    return data;
  } finally {
    clearTimeout(timer);
  }
}

async function dbFinishGroup(sessionCode, groupName) {
  const { data, error } = await supabase.rpc("finish_group", {
    p_code: sessionCode, p_group_name: groupName,
    p_token: getGroupToken(sessionCode, groupName),
  });
  if (error) throw error;
  return data;
}

// Map a raw sessions row (snake_case, teacher/colleague read) to the shape
// TRATEditor and the teacher lists expect. Shared so kursvyn kan öppna samma
// editor som "Mina tRAT:ar" utan att duplicera mappningen.
function mapTeacherSessionRow(d) {
  return {
    sessionId: d.id, dashId: d.dash_id, leaderboardId: d.leaderboard_id || null,
    numQ: d.num_questions,
    createdAt: d.created_at, sessionTitle: d.session_title,
    availableAt: d.available_at, answerKey: d.answer_key,
    numOptions: d.num_options, questions: d.questions,
    config: d.config || {}, sessionLocked: d.session_locked || false,
    courseInstanceId: d.course_instance_id || null,
    teacherId: d.teacher_id || null,
  };
}

async function dbLoadTeacherSessions(userId) {
  const { data } = await supabase.from("sessions").select("*").eq("teacher_id", userId).order("created_at", { ascending: false });
  return (data || []).map(mapTeacherSessionRow);
}

// PostgREST lägger .in()-listan i URL:en; dela upp långa listor så att en
// programadmin med hundratals tRAT:ar inte slår i URL-längden.
async function inChunks(ids, fn, size = 100) {
  const out = [];
  for (let i = 0; i < ids.length; i += size) out.push(...(await fn(ids.slice(i, i + size))));
  return out;
}

// Startsidans lista: egna tRAT:ar plus kollegornas i de kurstillfällen man har
// tillgång till (samma urval som kursvyn). Egna utan kurs kommer med via
// teacher_id.
async function dbLoadVisibleTeacherSessions(userId, instanceIds = []) {
  const [{ data: own }, inCourses] = await Promise.all([
    supabase.from("sessions").select("*").eq("teacher_id", userId),
    inChunks(instanceIds, async ids => {
      const { data } = await supabase.from("sessions").select("*").in("course_instance_id", ids);
      return data || [];
    }),
  ]);
  const byId = new Map();
  for (const row of [...(own || []), ...inCourses]) byId.set(row.id, row);
  return [...byId.values()]
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .map(mapTeacherSessionRow);
}

// Gruppaktivitet per tRAT: { [sessionId]: { active, done } }
async function dbLoadGroupActivity(sessionIds) {
  const rows = await inChunks(sessionIds, async ids => {
    const { data } = await supabase.from("group_results").select("session_id, in_progress").in("session_id", ids);
    return data || [];
  });
  const m = {};
  for (const r of rows) {
    const e = m[r.session_id] || (m[r.session_id] = { active: 0, done: 0 });
    if (r.in_progress) e.active++; else e.done++;
  }
  return m;
}

// Obesvarade appeals per tRAT: { [sessionId]: antal }
async function dbLoadPendingAppealCounts(sessionIds) {
  const rows = await inChunks(sessionIds, async ids => {
    const { data } = await supabase.from("appeals").select("session_id").eq("status", "pending").in("session_id", ids);
    return data || [];
  });
  const m = {};
  for (const r of rows) m[r.session_id] = (m[r.session_id] || 0) + 1;
  return m;
}

// Namn på den som skapade varje tRAT: { [sessionId]: namn }. Saknas
// funktionen (migrationen ej körd) blir det tomt och raden visar "Kollega".
async function dbLoadSessionTeacherNames(sessionIds) {
  if (sessionIds.length === 0) return {};
  const rows = await inChunks(sessionIds, async ids => {
    const { data, error } = await supabase.rpc("session_teacher_names", { p_session_ids: ids });
    if (error) return [];
    return data || [];
  });
  return Object.fromEntries(rows.filter(r => r.teacher_name).map(r => [r.session_id, r.teacher_name]));
}

// ─────────────────────────────────────────
//  Course & Profile DB helpers
// ─────────────────────────────────────────
async function dbLoadUserCourses(userId) {
  // Behörigheten ligger i RLS (can_access_course): superadmin ser allt,
  // programadmin sitt program, kursansvarig sin kurs. Klienten filtrerade
  // förut på egen hand utifrån user_roles och ORade org/program/kurs — vilket
  // gav en kursansvarig hela organisationen. Två källor till samma sanning är
  // en av dem för mycket; frågan returnerar nu exakt det man får se.
  const { data } = await supabase.from("course_instances")
    .select(`
      id, name, term, year, course_id,
      courses (id, name, program_id,
        programs (id, name, org_id,
          organisations (id, name)
        )
      )
    `)
    .order("year", { ascending: false });
  return data || [];
}

// Kurserna läraren undervisar på. Till skillnad från dbLoadUserCourses (som
// utgår från kurstillfällen) syns en kurs här även innan den har något
// tillfälle — tillfällen skapas numera automatiskt vid behov.
async function dbLoadUserCourseList(userId) {
  // Som ovan: RLS avgör vilka kurser som kommer tillbaka.
  const { data } = await supabase.from("courses")
    .select("id, name, program_id, programs (id, name, org_id, organisations (id, name))")
    .order("name");
  return data || [];
}

// Hämta (eller skapa) kursens tillfälle för aktuell termin. Läraren väljer bara
// kurs; terminen härleds serverside.
async function dbEnsureCourseInstance(courseId) {
  const { data, error } = await supabase.rpc("ensure_course_instance", { p_course_id: courseId });
  if (error) { console.error("ensureCourseInstance:", error); return null; }
  if (!data || data.error) { console.error("ensureCourseInstance:", data?.error); return null; }
  return data; // { id, name, term, year }
}

// Vad användaren får administrera. Superadmin: allt. Programadmin: sina
// program (inklusive att tilldela roller där). Kursansvarig: ingenting av
// detta — de administrerar inga användare.
async function dbLoadAdminScope(userId) {
  if (!userId) return { isSuperAdmin: false, programIds: [] };
  const { data, error } = await supabase.from("user_roles")
    .select("role, program_id").eq("user_id", userId);
  // Ett nekat eller trasigt anrop får inte tyst degraderas till "ingen
  // behörighet" — då ser det ut som att rollen försvunnit i stället för att
  // något är fel med frågan.
  if (error) console.error("loadAdminScope:", error);
  const rows = data || [];
  return {
    isSuperAdmin: rows.some(r => r.role === "superadmin"),
    programIds: rows.filter(r => r.role === "program_admin" && r.program_id).map(r => r.program_id),
  };
}

async function dbLoadUserProfile(userId) {
  const { data } = await supabase.from("user_roles").select("role, org_id, program_id, course_id, organisations(name), programs(name), courses(name)").eq("user_id", userId);
  return data || [];
}

// Alla tRAT:ar för en kurs, oavsett termin. Varje rad får med sitt tillfälles
// term/år så kursvyn kan gruppera per termin.
async function dbLoadSessionsByCourse(courseId) {
  const { data: instances } = await supabase.from("course_instances")
    .select("id, name, term, year").eq("course_id", courseId);
  if (!instances || instances.length === 0) return [];
  const byId = Object.fromEntries(instances.map(i => [i.id, i]));
  const sessions = await dbLoadSessionsByCourseInstance(instances.map(i => i.id));
  return sessions.map(s => ({ ...s, _instance: byId[s.course_instance_id] || null }));
}

// instanceId kan vara ett id eller en array av id:n
async function dbLoadSessionsByCourseInstance(instanceId) {
  const ids = Array.isArray(instanceId) ? instanceId : [instanceId];
  if (ids.length === 0) return [];
  const { data } = await supabase
    .from("sessions")
    .select("*")
    .in("course_instance_id", ids)
    .order("created_at", { ascending: false });
  if (!data) return [];

  // For each session, fetch lightweight result stats (count + avg score)
  const enriched = await Promise.all(data.map(async s => {
    const { data: results } = await supabase
      .from("group_results")
      .select("total, max_score, in_progress")
      .eq("session_id", s.id);
    const all = results || [];
    const completed = all.filter(r => !r.in_progress);
    const active = all.filter(r => r.in_progress);
    const avgScore = completed.length > 0
      ? completed.reduce((sum, r) => sum + (r.total || 0), 0) / completed.length
      : null;
    const maxScore = completed.length > 0 ? (completed[0]?.max_score || null) : null;
    return { ...s, _stats: { total: all.length, completed: completed.length, active: active.length, avgScore, maxScore } };
  }));
  return enriched;
}


// ─────────────────────────────────────────
//  Question Bank DB helpers
// ─────────────────────────────────────────

// Upload a bank question's figures under bank/<questionId>/. Bank questions get
// their OWN copies: pointing at a session's files would break them as soon as
// that tRAT is deleted (dbDeleteQuestionImages clears the session prefix).
// Accepts freshly parsed blobs as well as URLs from an already-saved session,
// which are fetched and re-uploaded.
async function dbUploadBankImages(questionId, images) {
  if (!Array.isArray(images) || images.length === 0) return [];
  const urls = [];
  for (let i = 0; i < images.length; i++) {
    const img = images[i];
    let blob = img?.blob || null;
    if (!blob && typeof img === "string") {
      if (img.includes(`/bank/${questionId}/`)) { urls.push(img); continue; } // redan vår egen
      try {
        const res = await fetch(img);
        if (res.ok) blob = await res.blob();
      } catch (err) { console.error("dbUploadBankImages fetch:", err); }
    }
    if (!blob) continue;
    const path = `bank/${questionId}/${i + 1}.jpg`;
    const { error } = await supabase.storage.from("question-images")
      .upload(path, blob, { contentType: "image/jpeg", upsert: true });
    if (error) { console.error("dbUploadBankImages:", error); continue; }
    const { data } = supabase.storage.from("question-images").getPublicUrl(path);
    if (data?.publicUrl) urls.push(data.publicUrl);
  }
  return urls;
}

async function dbDeleteBankImages(questionId) {
  try {
    const prefix = `bank/${questionId}`;
    const { data: files } = await supabase.storage.from("question-images").list(prefix);
    if (files && files.length) {
      await supabase.storage.from("question-images")
        .remove(files.map(f => `${prefix}/${f.name}`));
    }
  } catch (err) {
    console.error("dbDeleteBankImages:", err);
  }
}

async function dbLoadQuestionBank(userId) {
  const { data } = await supabase
    .from("question_bank")
    .select("*, question_visibility(*), question_tags(*, tags(*))")
    .eq("is_latest", true)
    .order("created_at", { ascending: false });
  return data || [];
}

async function dbSaveQuestionToBank(question, userId, courseInstanceId, courseId, programId, orgId) {
  // Determine scope
  let scope = "private";
  if (courseId) scope = "course";
  if (programId && !courseId) scope = "program";
  if (orgId && !programId && !courseId) scope = "org";
  if (courseInstanceId && courseId) scope = "course";

  const { data, error } = await supabase
    .from("question_bank")
    .insert({
      question_text: question.text || "",
      options: question.options || {},
      correct_answer: question.correctAnswer || null,
      explanation: question.rationale || "",
      created_by: userId,
      is_latest: true,
      version_nr: 1,
    })
    .select()
    .single();

  if (error || !data) { console.error("dbSaveQuestionToBank:", error); return null; }

  // Figures: upload our own copies now that we have the question id
  if (question.images?.length) {
    const urls = await dbUploadBankImages(data.id, question.images);
    if (urls.length) {
      const { error: imgErr } = await supabase.from("question_bank")
        .update({ images: urls }).eq("id", data.id);
      if (imgErr) console.error("dbSaveQuestionToBank images:", imgErr);
      else data.images = urls;
    }
  }

  // Set visibility
  await supabase.from("question_visibility").insert({
    question_id: data.id,
    scope,
    course_id: courseId || null,
    program_id: programId || null,
    org_id: orgId || null,
  });

  return data;
}

// Taggar är personliga. Filtret var tidigare
// `created_by.eq.<uid>,org_id.is.null` — men createTag skickade alltid
// org_id = null, så det andra villkoret matchade ALLA taggar och varje lärares
// taggar blev synliga för samtliga lärare i systemet. created_by-villkoret var
// i praktiken dött. Nu: bara egna taggar (och ingen strängkonkatenering i
// filtret, samma mönster som fixades i dbDeleteGroup).
async function dbLoadTags(userId) {
  const { data } = await supabase
    .from("tags")
    .select("*")
    .eq("created_by", userId)
    .order("name");
  return data || [];
}

async function dbCreateTag(name, userId) {
  const { data, error } = await supabase.from("tags")
    .insert({ name, created_by: userId })
    .select().single();
  if (error) { console.error("dbCreateTag:", error); return null; }
  return data;
}

async function dbAddTagToQuestion(questionId, tagId) {
  await supabase.from("question_tags").upsert({ question_id: questionId, tag_id: tagId });
}

async function dbRemoveTagFromQuestion(questionId, tagId) {
  await supabase.from("question_tags").delete().eq("question_id", questionId).eq("tag_id", tagId);
}

async function dbDeleteQuestion(questionId) {
  await supabase.from("question_bank").delete().eq("id", questionId);
  await dbDeleteBankImages(questionId); // annars blir figurerna föräldralösa
}

async function dbUpdateQuestion(questionId, updates) {
  await supabase.from("question_bank").update(updates).eq("id", questionId);
}


async function dbToggleRationale(sessionId, show) {
  const { error } = await supabase.from("sessions").update({ show_rationale: show }).eq("id", sessionId);
  if (error) console.error("toggleRationale:", error);
}

async function dbToggleLock(sessionId, locked) {
  const { data, error } = await supabase.from("sessions").update({ session_locked: locked }).eq("id", sessionId).select();
  if (error) { console.error("toggleLock:", error); throw error; }
  if (!data || data.length === 0) console.warn("toggleLock: no rows updated – check RLS policies");
}

async function dbDeleteSession(sessionId) {
  const { error } = await supabase.from("sessions").delete().eq("id", sessionId);
  if (error) { console.error("deleteSession:", error); return false; }
  await dbDeleteQuestionImages(sessionId); // annars blir bilderna föräldralösa
  return true;
}

async function dbUpdateSessionTitle(sessionId, title) {
  const { error } = await supabase.from("sessions").update({ session_title: title }).eq("id", sessionId);
  if (error) console.error("updateTitle:", error);
}

async function dbUpdateSession(sessionId, updates) {
  const payload = "questions" in updates
    ? { ...updates, questions: stripAnswerKeys(updates.questions) }
    : updates;
  const { error } = await supabase.from("sessions").update(payload).eq("id", sessionId);
  if (error) console.error("updateSession:", error);
  return !error;
}

async function dbLoadSessionStats(sessionId) {
  const { data } = await supabase.from("group_results").select("total, max_score, in_progress").eq("session_id", sessionId);
  if (!data || data.length === 0) return null;
  const done = data.filter(d => !d.in_progress);
  const avgPct = done.length > 0 ? Math.round(done.reduce((s, d) => s + (d.total / (d.max_score || 1)), 0) / done.length * 100) : null;
  return { count: data.length, doneCount: done.length, avgPct };
}

// Timern ligger i sessionens config. Dashboarden skrev förut tillbaka sin egen
// kopia av hela config, inläst när den öppnades — så allt läraren ändrat i
// Redigera sedan dess (grupper, gruppkoder, slump) skrevs över när timern
// startades, förlängdes eller stoppades. Nu läses aktuell config precis före
// skrivningen och bara timer_end ändras.
async function dbSetTimer(sessionId, timerEnd) {
  const { data: cur, error: readErr } = await supabase.from("sessions")
    .select("config").eq("id", sessionId).maybeSingle();
  if (readErr) { console.error("setTimer:", readErr); throw readErr; }
  if (!cur) { console.warn("setTimer: session not readable – check RLS policies"); return; }
  const config = { ...(cur.config || {}), timer_end: timerEnd };
  const { data, error } = await supabase.from("sessions").update({ config }).eq("id", sessionId).select("id");
  if (error) { console.error("setTimer:", error); throw error; }
  if (!data || data.length === 0) console.warn("setTimer: no rows updated – check RLS policies");
}

async function dbCheckIfStarted(sessionId) {
  const results = await dbLoadAllResults(sessionId);
  return results.length > 0;
}

// Teacher adjusts one question's score (appeal approval). Updates the scores
// array, total AND scratch_state[qi].score so the row stays internally
// consistent — earlier this overwrote scratch_state with NULL and wiped the
// group's progress.
async function dbAdjustGroupScore(sessionId, groupName, qi, adjusted) {
  const { data } = await supabase.from("group_results").select("scores, scratch_state")
    .eq("session_id", sessionId).eq("group_name", groupName).maybeSingle();
  if (!data) return null;
  const scores = [...(data.scores || [])];
  scores[qi] = adjusted;
  const total = scores.reduce((s, v) => s + (v ?? 0), 0);
  const updates = { scores, total };
  if (Array.isArray(data.scratch_state)) {
    updates.scratch_state = data.scratch_state.map((q, i) =>
      i === qi && q && typeof q === "object" ? { ...q, score: adjusted } : q);
  }
  const { error } = await supabase.from("group_results").update(updates)
    .eq("session_id", sessionId).eq("group_name", groupName);
  if (error) { console.error("adjustGroupScore:", error); return null; }
  return { scores, total };
}

// Own group's full state (attempts/scratch_state). Students can no longer read
// group_results directly — this goes through a token-gated RPC so no group can
// read another group's attempts (which would reveal the answer key: score 4
// means the first scratched option was correct).
async function dbLoadGroupResult(sessionId, groupName) {
  const { data, error } = await supabase.rpc("get_group_state", {
    p_code: sessionId, p_group: groupName, p_token: getGroupToken(sessionId, groupName),
  });
  if (error || !data || data.error) return null;
  return mapGroupResultRow(data);
}

async function dbSaveGroupTimer(sessionId, groupName, timerEnd) {
  const { error } = await supabase.from("group_results")
    .update({ timer_end: timerEnd })
    .eq("session_id", sessionId).eq("group_name", groupName);
  if (error) console.error("saveGroupTimer:", error);
}

async function dbExtendAllGroupTimers(sessionId, extraMins) {
  // Load all groups that have a per-group timer, extend each by extraMins
  const { data, error } = await supabase.from("group_results").select("group_name, timer_end").eq("session_id", sessionId);
  if (error) { console.error("extendGroupTimers:", error); throw error; }
  if (!data) return;
  const now = serverNow();
  const updates = data.filter(d => d.timer_end).map(d => {
    const base = Math.max(new Date(d.timer_end).getTime(), now);
    return { group_name: d.group_name, timer_end: new Date(base + extraMins * 60 * 1000).toISOString() };
  });
  if (updates.length === 0) return;
  const results = await Promise.all(updates.map(u =>
    supabase.from("group_results").update({ timer_end: u.timer_end })
      .eq("session_id", sessionId).eq("group_name", u.group_name)
  ));
  const failed = results.find(r => r.error);
  if (failed) { console.error("extendGroupTimers:", failed.error); throw failed.error; }
}

async function dbLoadAllResults(sessionId) {
  const { data } = await supabase.from("group_results").select("*").eq("session_id", sessionId);
  return (data || []).map(d => ({ groupName: d.group_name, sessionId: d.session_id, scores: d.scores, attempts: d.attempts, total: d.total, maxScore: d.max_score, inProgress: d.in_progress, scratchState: d.scratch_state, updatedAt: d.updated_at || null }));
}

// Gruppstatus för anslutningsskärmen. Går via RPC eftersom gruppnamn inte
// längre ligger i den anonymt läsbara group_progress — sessionskoden är
// kapabiliteten som ger tillgång till namnen.
async function dbLoadActiveGroups(sessionId) {
  const { data, error } = await supabase.rpc("get_active_groups", { p_code: sessionId });
  if (error) { console.error("loadActiveGroups:", error); return []; }
  return data || [];
}

// Resultattavla (projektorvy) — samma sak, via leaderboard- eller sessionskod.
async function dbLoadLeaderboard(sessionId) {
  const { data, error } = await supabase.rpc("get_leaderboard", { p_code: sessionId });
  return { rows: data || [], error };
}

// Egen grupps appeals, burning questions och delegationer. Ersätter fyra
// direktselects som krävde att tabellerna var anonymt läsbara.
async function dbLoadGroupFeedback(sessionId, groupName) {
  const { data, error } = await supabase.rpc("get_group_feedback", {
    p_code: sessionId, p_group: groupName, p_token: getGroupToken(sessionId, groupName),
  });
  // null vid fel, så att anroparen behåller det den har (och hämtloopen
  // glesar ut) i stället för att tömma listorna
  if (error || !data || data.error) {
    if (error) console.error("loadGroupFeedback:", error);
    return null;
  }
  const map = d => ({ ...d, groupName: d.group_name, questionIndex: d.question_index,
                      iFATQuestion: d.ifat_question, toGroup: d.to_group, _key: d.id });
  return {
    appeals: (data.appeals || []).map(map),
    burningQuestions: (data.burning_questions || []).map(map),
    delegationsIn: (data.delegations_in || []).map(d => ({ ...map(d), groupName: d.from_group })),
    delegationsOut: (data.delegations_out || []).map(d => ({ ...map(d), groupName: d.from_group })),
  };
}

// Dashboard authorization, decided server-side: true for the owner, for a
// colleague teaching the same course, and for legacy sessions without a teacher
// (there the secret dash code is the capability).
async function dbCanViewSession(sessionId) {
  const { data, error } = await supabase.rpc("can_view_session", { p_session_id: sessionId });
  if (error) { console.error("canViewSession:", error); return false; }
  return data === true;
}

// Dashboard results via the dash code capability, so the dashboard also works
// for legacy sessions without a teacher_id where the owner policy can't match.
async function dbLoadAllResultsForDash(dashId) {
  const { data, error } = await supabase.rpc("get_dash_results", { p_dash: dashId });
  if (error) { console.error("loadAllResultsForDash:", error); return []; }
  return (data || []).map(d => ({ groupName: d.group_name, sessionId: d.session_id, scores: d.scores, attempts: d.attempts, total: d.total, maxScore: d.max_score, inProgress: d.in_progress, scratchState: d.scratch_state, updatedAt: d.updated_at || null }));
}

async function dbRenameGroup(sessionId, oldName, newName) {
  await supabase.from("group_results").update({ group_name: newName }).eq("session_id", sessionId).eq("group_name", oldName);
  await supabase.from("appeals").update({ group_name: newName }).eq("session_id", sessionId).eq("group_name", oldName);
  await supabase.from("burning_questions").update({ group_name: newName }).eq("session_id", sessionId).eq("group_name", oldName);
  await supabase.from("delegations").update({ from_group: newName }).eq("session_id", sessionId).eq("from_group", oldName);
  await supabase.from("delegations").update({ to_group: newName }).eq("session_id", sessionId).eq("to_group", oldName);
}

async function dbDeleteGroup(sessionId, groupName) {
  // Two eq-filtered deletes instead of a string-interpolated .or() — a group
  // name containing a comma/parenthesis would otherwise corrupt the PostgREST
  // filter and delete the wrong rows.
  await supabase.from("delegations").delete().eq("session_id", sessionId).eq("from_group", groupName);
  await supabase.from("delegations").delete().eq("session_id", sessionId).eq("to_group", groupName);
  await supabase.from("burning_questions").delete().eq("session_id", sessionId).eq("group_name", groupName);
  await supabase.from("appeals").delete().eq("session_id", sessionId).eq("group_name", groupName);
  await supabase.from("group_results").delete().eq("session_id", sessionId).eq("group_name", groupName);
}

// Student submits an appeal — via a token-gated RPC (students can no longer
// write the appeals table directly, nor set the status).
async function dbSaveAppeal(sessionId, groupName, qi, type, fields) {
  return withRetry(async () => {
    const { data, error } = await supabase.rpc("submit_appeal", {
      p_code: sessionId, p_group: groupName, p_token: getGroupToken(sessionId, groupName),
      p_qi: qi, p_type: type, p_fields: fields,
    });
    if (error) throw error;
    if (data?.error) throw new Error(data.error);
  }, { label: "saveAppeal" });
}

async function dbLoadAllAppeals(sessionId) {
  const { data } = await supabase.from("appeals").select("*").eq("session_id", sessionId);
  return (data || []).map(d => ({ ...d, groupName: d.group_name, questionIndex: d.question_index, iFATQuestion: d.ifat_question, _key: d.id }));
}

async function dbUpdateAppeal(id, status, adjustedScore) {
  const { error } = await supabase.from("appeals").update({ status, adjusted_score: adjustedScore }).eq("id", id);
  if (error) console.error("updateAppeal:", error);
}

async function dbSaveBQ(sessionId, groupName, qi, text) {
  return withRetry(async () => {
    const { data, error } = await supabase.rpc("submit_bq", {
      p_code: sessionId, p_group: groupName, p_token: getGroupToken(sessionId, groupName),
      p_qi: qi, p_text: text,
    });
    if (error) throw error;
    if (data?.error) throw new Error(data.error);
  }, { label: "saveBQ" });
}

async function dbLoadAllBQ(sessionId) {
  const { data } = await supabase.from("burning_questions").select("*").eq("session_id", sessionId);
  return (data || []).map(d => ({ ...d, groupName: d.group_name, questionIndex: d.question_index, iFATQuestion: d.ifat_question, _key: d.id }));
}

async function dbSaveDelegation(sessionId, bqId, toGroup, fromGroup, text, iFATQuestion) {
  return withRetry(async () => {
    const { error } = await supabase.from("delegations").insert({ session_id: sessionId, bq_id: bqId, from_group: fromGroup, to_group: toGroup, text, ifat_question: iFATQuestion });
    if (error) throw error;
  }, { label: "saveDelegation" });
}


async function dbLoadAllDelegations(sessionId) {
  const { data } = await supabase.from("delegations").select("*").eq("session_id", sessionId);
  return (data || []).map(d => ({ ...d, toGroup: d.to_group, groupName: d.from_group, iFATQuestion: d.ifat_question, _key: d.id }));
}


async function dbAnswerDelegation(sessionId, groupName, id, answer) {
  const { data, error } = await supabase.rpc("answer_delegation", {
    p_id: id, p_group: groupName, p_token: getGroupToken(sessionId, groupName), p_answer: answer,
  });
  if (error) { console.error("answerDelegation:", error); return false; }
  if (data?.error) { console.error("answerDelegation:", data.error); return false; }
  return true;
}

export { forgetGroup, dbEnsureGroupRef, dbLoadLiveSession, dbLoadSessionStatus, mapTeacherSessionRow, dbLoadVisibleTeacherSessions, dbLoadGroupActivity, dbLoadPendingAppealCounts, dbLoadSessionTeacherNames, dbLoadAdminScope, dbPruneQuestionImages, dbSaveSession, dbCreateSessionWithFreshIds, dbUploadQuestionImages, dbDeleteQuestionImages, dbLoadSession, mapGroupResultRow, dbJoinGroup, dbRecordScratch, dbFinishGroup, dbLoadTeacherSessions, dbLoadUserCourses, dbLoadUserCourseList, dbEnsureCourseInstance, dbLoadUserProfile, dbLoadSessionsByCourseInstance, dbLoadSessionsByCourse, dbLoadQuestionBank, dbSaveQuestionToBank, dbUploadBankImages, dbDeleteBankImages, dbLoadTags, dbCreateTag, dbAddTagToQuestion, dbRemoveTagFromQuestion, dbDeleteQuestion, dbUpdateQuestion, dbToggleRationale, dbToggleLock, dbDeleteSession, dbUpdateSessionTitle, dbUpdateSession, dbLoadSessionStats, dbSetTimer, dbCheckIfStarted, dbAdjustGroupScore, dbLoadGroupResult, dbSaveGroupTimer, dbExtendAllGroupTimers, dbLoadAllResults, dbLoadAllResultsForDash, dbCanViewSession, dbLoadActiveGroups, dbLoadLeaderboard, dbLoadGroupFeedback, getGroupRef, dbRenameGroup, dbDeleteGroup, dbSaveAppeal, dbLoadAllAppeals, dbUpdateAppeal, dbSaveBQ, dbLoadAllBQ, dbSaveDelegation, dbLoadAllDelegations, dbAnswerDelegation };
